import React, {useState, useEffect} from "react"
import {Map} from "./Map.js"
import nextSection from "./res/img/next.svg"
import {appIntro as appIntro311, sections as sections311, sectionOrder as sectionOrder311} from "./sections_311.js"
import {appIntro as appIntroScoring, sections as sectionsScoring, sectionOrder as sectionOrderScoring} from "./sections_scoring.js"
import "mapbox-gl/dist/mapbox-gl.css"
import "./App.css"
import {hexes, CATEGORY_NAMES} from "./data.js"
import * as d3 from "d3"
import MapLegend from "./MapLegend.js"

const MapOverlay = ({children}) => {
  return <div className="MapOverlay">{children}</div>
}

const App = () => {
  let features = hexes.features
  // Whether to show the 'hover the map' mesage
  let [hoveredBefore, setHoveredBefore] = useState(false) //
  // Whether the user has picked one of the two paths
  let [pathPicked, setPathPicked] = useState('?')

  // Make sure to reset the hoveredBefore indicator when the sections reset.
  let [sectionIndex, _setSectionIndex] = useState(null)
  let setSectionIndex = value => {
    // reset the hover indicator every time we go back to the first screen
    if (value == 0 ) {
      setHoveredBefore(false)
      setPathPicked('?')
    }
    _setSectionIndex(value)
  }

  // The module name is '311' or 'scoring'
  let [moduleName, setModuleName] = useState('311')

  let sections = {'311': sections311, 'scoring': sectionsScoring}[moduleName]
  let sectionOrder = {'311': sectionOrder311, 'scoring': sectionOrderScoring}[moduleName]
  let section = sections[sectionOrder[sectionIndex || 0]]

  // Section state is specified in the individual modules.
  let [sectionState, setSectionState] = useState(() => section.defaultState(features))

  // Reset section state when sections change
  useEffect(() => setSectionState(section.defaultState(features)), [sectionIndex])

  let {property, color, legendFormat, composition} = sectionState
  let numSections = sectionOrder.length

  // The map hover
  let [hoverFeatures, setHoverFeatures] = useState([])
  if (hoverFeatures.length && !hoveredBefore) {
    setHoveredBefore(true)
  }

  const wat = (
    <>
      <h1>Data-driven Boston</h1>
      <div className="App-subtitle">Making decisions using data</div>
      <div className="App-intro-ps">
        <p>What will the future be like? Will there be flying cars? Will services, like education and street maintenance, be equitable?</p>
        <p>Beta Blocks is a participatory imagination project for Boston communities to question and explore the role of data and new technologies in the places they live.</p>
        <p>This application allows users to explore the hidden stories of Boston{"'"}s communities in 311 data or to make a custom decision-making algorithm to determine which communities should receive public realm improvements first. Information can be very powerful but we are just learning how to effectively and equitably use it. Please join us!</p>

        <div className={"App-module-picker m-" + moduleName}>
          <div className='m-scoring' onClick={() => {setModuleName('311'); setPathPicked('311');}}>Explore 311 Data</div>
          <div className='m-scoring' onClick={() => {setModuleName('scoring'); setPathPicked('scoring')}}>Explore Decision-making</div>
        </div>
      </div>
    </>
  )


  let appIntro = pathPicked === '?' ? wat : pathPicked === '311' ? appIntro311 : appIntroScoring
  // let appIntro = moduleName == '311' ? appIntro311 : appIntroScoring

  return (
    <div className={"App " + sectionOrder[sectionIndex || 0]}>

      {sectionIndex === null && (
        <div className="App-intro">
     
          {appIntro}
          {pathPicked !== '?' && 
          <div className="App-get-started" onClick={() => setSectionIndex(0) }>
            <NextButton />
            <div>Get Started »</div>
          </div>}
        </div>
      )}

      {sectionIndex !== null && [
        <Sidebar key="sidebar">

          {section.body({features, sectionState, setSectionState, hexes, property, color})}
          <SectionNavigator
            title={
              sectionIndex === numSections - 1
                ? "Back to the beginning"
                : sections[sectionOrder[sectionIndex + 1]].buttonTitle
            }
            sectionOrder={sectionOrder}
            sectionIndex={sectionIndex}
            setSectionIndex={setSectionIndex}
            numSections={numSections}
          />
        </Sidebar>,


        sectionIndex > 1 && sectionIndex < 5 && (
          <MapOverlay key="map-overlay">
            <MapLegend hexes={hexes} property={property} color={color} format={legendFormat} />
            {!hoveredBefore && <div className='hover-instruction'>{navigator.platform.match(/i(Phone|Pod)/i) ? "Tap on" : "Hover"} the map to inspect the data.</div>}
            {hoverFeatures.length > 0 && section.hoverInfo && section.hoverInfo({hoverFeatures, sectionState})}
          </MapOverlay>
        )
      ]}

      <Map
        composition={composition}
        hexes={hexes}
        property={property}
        color={color}
        stroke={!!sectionState.stroke}
        opacity={sectionIndex === null ? 0 : sectionState.opacity}
        sectionIndex={sectionIndex}
        showYouAreHere={sectionIndex === 2}
        showNeighborhoods={sectionIndex > 1}
        clickZoomable={moduleName == '311' && sectionIndex == 5}
        fadeOpacity={
          sectionIndex !== null && sectionState.fadeOpacity !== undefined
            ? sectionState.fadeOpacity
            : 0.65
        }
        onHover={features => setHoverFeatures(features)}
        onUnhover={features => setHoverFeatures([])}
      />
    </div>
  )
}

const Sidebar = ({children}) => <div className="App-sidebar">{children}</div>

const SectionNavigator = ({title, sectionIndex, setSectionIndex, numSections, sectionOrder}) => {
  let activeIf = target => (sectionIndex === target ? " active" : "")
  let lastSection = sectionIndex === numSections - 1
  return (
    <div className="SectionNavigator">
      <div
        onClick={() =>
          setSectionIndex(lastSection ? null : (sectionIndex + 1) % numSections)
        }
        className="SectionNavigator-main">
        <div className="SectionNavigator-heading">
          <div className="App-subtitle">
            {lastSection ? "Final section" : "Next section"}
          </div>
          <div className="SectionNavigator-title">
            {lastSection ? "To the Beginning" : title}
          </div>
        </div>
        <NextButton flip={lastSection} />
      </div>
      <div className="SectionNavigator-dots">
        {d3.range(sectionOrder.length).map(n => (
          <div
            key={n}
            onClick={() => setSectionIndex(n)}
            className={"SectionNavigator-dot" + activeIf(n)}
          />
        ))}
      </div>
    </div>
  )
}

const NextButton = ({flip, onClick}) => (
  <div onClick={onClick} className={"NextButton" + (flip ? " flip" : "")}>
    <img alt="" src={nextSection} height={20} />
  </div>
)

export default App
